import { ChangeDetectorRef, Component, NgZone, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { observeMediaQuery } from '@cmps/cdk';
import { isValid } from 'date-fns';
import { merge, Subject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import { DateProviders } from '../../common/component/datetime-picker';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { SearchUserContainerComponent } from '../../user/search-user.container.component';
import { User } from '../../user/user.model';
import { CmpsCardComponent } from '../card';
import { SearchItem, SearchType } from '../core';
import { Column, SortOrder } from '../grid';
import { useSelector } from '../store-use-selector';

import { createTableData } from './function/create-grid-data.function';
import { ColumnId } from './model/column-id.model';
import { TableData } from './model/table-data.model';
import { TranslationKeyMap } from './search.i18n';
import { searchReducer } from './search.reducer';
import { SearchState } from './search.state';
import { loadMore } from './state/load-more.action';
import { reload } from './state/reload.action';
import { reset } from './state/reset.action';
import { runSearch } from './state/run-search.action';
import { selectUser } from './state/select-user.action';
import { sortRows } from './state/sort-rows.action';

@Component({
    selector: 'cmps-eval-search',
    templateUrl: './search.component.html',
    host: {
        class: 'cmps-eval-search',
    },
    providers: [DateProviders],
})
@TakeUntilDestroy()
@UseStore<SearchState>('evaluation.search', searchReducer)
export class SearchComponent
    implements StateToProps<SearchState>, TakeUntilDestroy
{
    state: SearchState;

    @ViewChild('userSearchCard', { read: CmpsCardComponent })
    private readonly _userSearchCard: CmpsCardComponent;

    @ViewChild('searchResultsCard', { read: CmpsCardComponent })
    private readonly _searchResultsCard: CmpsCardComponent;

    @ViewChild('userSearch', { read: SearchUserContainerComponent })
    private readonly _userSearch: SearchUserContainerComponent;

    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _SearchType = SearchType;

    /** @private */
    _ColumnId = ColumnId;

    /** @private */
    _SortOrder = SortOrder;

    /** @private */
    _searchType: SearchType;

    /** @private */
    _dateRangeStart: Date;

    /** @private */
    _dateRangeEnd: Date;

    /** @private */
    _tableData: TableData;

    private _cancellationObservable: Subject<void>;

    /** collapse the search card when search card and result card
     * are not side by side when the search button is clicked */
    private _autoCollapseSearch: boolean;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _router: Router,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _authService: AuthService,
    ) {}

    ngOnInit(): void {
        useSelector<{
            value?: User;
            initialLoad?: boolean;
        }>('evaluation.search.selectedUser', () => null)
            .pipe(
                // skip(1), // ignore initial value when connecting to store
                tap((state) => {
                    this._changeDetectorRef.detectChanges();
                    if (state?.value == null) {
                        this._userSearchCard?.expand();
                        this._searchResultsCard?.expand();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useSelector<{
            value?: {
                start?: Date;
                end?: Date;
            };
            initialLoad?: boolean;
        }>('evaluation.search.selectedDateRange', () => null)
            .pipe(
                tap((state) => {
                    this._searchType = this.state?.selectedSearchType?.value;

                    this._dateRangeStart = state?.value?.start;
                    this._dateRangeEnd = state?.value?.end;

                    try {
                        this._changeDetectorRef.detectChanges();
                    } catch {}
                    // form is loaded (child router outlet)
                    if (
                        this.state.pending.runSearch !== true &&
                        this._activatedRoute.snapshot.children?.length > 0
                    ) {
                        this._userSearchCard?.collapse();
                        this._searchResultsCard?.collapse();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useSelector<boolean>('evaluation.search.pending.runSearch', () => null)
            .pipe(
                filter((state) => state === true),
                tap(() => {
                    if (this._autoCollapseSearch) {
                        this._userSearchCard?.collapse();
                    }
                    this._searchResultsCard?.expand();
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    this._router.navigate(['evaluation-search'], {
                        queryParams: {
                            userId: this.state?.selectedUser?.value?.id,
                            searchType: this.state?.selectedSearchType?.value,
                            dateRangeStart:
                                this.state?.selectedDateRange?.value?.start?.toJSON(),
                            dateRangeEnd:
                                this.state?.selectedDateRange?.value?.end?.toJSON(),
                        },
                    });
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useSelector<SearchItem[]>('evaluation.search.rows', () => null)
            .pipe(
                tap((state) => {
                    this._tableData = createTableData(
                        state,
                        this.state.sortOptions,
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                tap((e: NavigationEnd) => {
                    if (
                        e.url.includes('/evaluation-search/drafts') ||
                        e.url.includes('/evaluation-search/responses')
                    ) {
                        this._userSearchCard?.collapse();
                        this._searchResultsCard?.collapse();
                    }

                    const index = e.url.indexOf('?');
                    if (
                        e.url
                            .substring(0, index === -1 ? undefined : index)
                            .endsWith('/evaluation-search')
                    ) {
                        if (
                            this._autoCollapseSearch &&
                            (this.state?.pending?.runSearch === true ||
                                this.state?.rows != null)
                        ) {
                            this._userSearchCard?.collapse();
                        } else {
                            this._userSearchCard?.expand();
                        }
                        this._searchResultsCard?.expand();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        merge(
            // this._router.events.pipe(
            //     filter(e => e instanceof NavigationEnd),
            //     filter((e: NavigationEnd) => {
            //         // const index = e.url.indexOf('?');
            //         // return e.url.substring(0, index === -1 ? undefined : index).endsWith('/evaluation-search');
            //         return e.url.endsWith('/evaluation-search');
            //     }),
            // ),
            this._activatedRoute.queryParamMap,
        )
            .pipe(
                tap(() => {
                    const queryParams =
                        this._activatedRoute.snapshot.queryParamMap;

                    let userId = parseInt(queryParams.get('userId'));
                    userId = !isNaN(userId) ? userId : null;
                    let user: User;
                    if (userId != null) {
                        user = new User();
                        user.id = userId;
                    }

                    let searchType = parseInt(queryParams.get('searchType'));
                    searchType = !isNaN(searchType) ? searchType : null;

                    let dateRangeStart = new Date(
                        queryParams.get('dateRangeStart') ?? NaN,
                    );
                    dateRangeStart = isValid(dateRangeStart)
                        ? dateRangeStart
                        : null;

                    let dateRangeEnd = new Date(
                        queryParams.get('dateRangeEnd') ?? NaN,
                    );
                    dateRangeEnd = isValid(dateRangeEnd) ? dateRangeEnd : null;

                    useDispatcher(
                        this,
                        reload({
                            user,
                            searchType,
                            dateRangeStart,
                            dateRangeEnd,
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._cancellationObservable = new Subject();
        observeMediaQuery(
            '(min-width: 1800px)',
            true,
            this._cancellationObservable,
        )
            .pipe(
                tap((matches) => {
                    this._autoCollapseSearch = !matches;

                    // ensure user search card is expanded when going >= 1800
                    if (
                        matches &&
                        (this._activatedRoute.snapshot.children?.length ??
                            0) === 0
                    ) {
                        this._userSearchCard?.expand();
                        this._changeDetectorRef.detectChanges();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._cancellationObservable?.next();
        this._cancellationObservable?.complete();

        useDispatcher(this, reset({}));
    }

    /** @private */
    _onUserSelect(user: User): void {
        useDispatcher(this, selectUser({ user }));
    }

    /** @private */
    _onSearchClick(): void {
        // TODO revise. calls private method
        this._userSearch?._onSelectClick();

        useDispatcher(
            this,
            runSearch({
                params: {
                    userId: this._authService.user?.id,
                    targetUserId: this.state?.selectedUser?.value?.id,
                    searchType: this._searchType,
                    dateRangeStart: this._dateRangeStart,
                    dateRangeEnd: this._dateRangeEnd,
                    timeZoneOffset: new Date().getTimezoneOffset(),
                },
            }),
        );
    }

    /** @private */
    _onColumnSortClick(c: Column, sortOrder: SortOrder = SortOrder.ASC): void {
        useDispatcher(
            this,
            sortRows({
                options: { columnId: c.id as ColumnId, order: sortOrder },
            }),
        );
    }

    /** @private */
    _onSelectRowClick(item: SearchItem): void {
        this._ngZone.run(() => {
            // useDispatcher(this, selectSearchItem({item}));
            this._userSearchCard?.collapse();
            this._searchResultsCard?.collapse();

            let cmds: any[] = null;
            if (item.draftId != null) {
                cmds = ['evaluation-search', 'drafts', item.draftId];
            }
            if (item.responseId != null) {
                cmds = ['evaluation-search', 'responses', item.responseId];
            }
            if (cmds == null) {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._router.navigate(cmds, { queryParamsHandling: 'merge' });
        });
    }

    /** @private */
    _onLoadMoreClick(): void {
        useDispatcher(this, loadMore({}));
    }
}
